import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import Joi from "joi-browser";
import moment from "moment";
import Input from "./Input";
import Select from "./Select";
import RadioButton from "./RadioButton";
import medicationOptionSets from "../../options/medications.json";
import { proxyCall } from "../services/soar";
import {
  getFieldDate,
  formatDate,
  createEntity,
  updateEntity,
  getFieldName,
  checkKeyPress,
} from "../../scripts/helperScripts";

function MedicationModal({
  isOpen,
  hideModal,
  nextModal,
  isLastModal,
  clientId,
  clientProgram,
  initialData,
  updateMedications,
}) {
  const clientProgramAdmitDate = new Date(
    clientProgram && getFieldDate(clientProgram.ccx_admitdate)
  );
  const [medication, setMedication] = useState({});
  const [searchType, setSearchType] = useState("startsWith");
  const [searchQuery, setSearchQuery] = useState();
  const [medicationOptions, setMedicationOptions] = useState([]);
  const [noMedications, setNoMedications] = useState(false);
  const [errors, setErrors] = useState({});
  const isMountedRef = useRef(true);
  const navigate = useNavigate();
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  const FDBOrderVersion = "FDBOrder_2_7";

  let joiSchema = {
    selectedMedication: Joi.string().required().label("Medication"),
    ccx_clientmedicationid: Joi.string().allow(""),
    ccx_genericid: Joi.string().max(12).allow("").label("Generic ID"),
    ccx_dose: Joi.number()
      .min(0)
      .label("Dose")
      .when("NoMedications", { is: false, then: Joi.required() }),
    ccx_doseunit_global: Joi.string()
      .label("Dose Unit")
      .when("NoMedications", { is: false, then: Joi.required() }),
    ccx_quantity: Joi.string().allow("").label("Quantity"),
    ccx_route_global: Joi.string()
      .label("Route")
      .when("NoMedications", { is: false, then: Joi.required() }),
    ccx_dosetiming: Joi.string().allow("").label("Frequency"),
    ccx_doseother: Joi.string().allow("").label("Frequency Unit"),
    ccx_sourceofdata: Joi.string()
      .label("Source of Data")
      .when("NoMedications", { is: false, then: Joi.required() }),
    ccx_reportedby: Joi.string().allow("").label("Reported By"),
    ccx_prescriber: Joi.string()
      .label("Prescriber")
      .when("NoMedications", { is: false, then: Joi.required() }),
    ccx_patientnotes: Joi.string().allow("").label("Patient Notes"),
    ccx_startdate: Joi.date()
      .max(
        moment(clientProgramAdmitDate).isAfter(moment())
          ? moment(clientProgramAdmitDate).toDate()
          : "now"
      )
      .min(new Date("1/1/1900"))
      .label("Start Date")
      .error((errors) => {
        errors.forEach((error) => {
          switch (error.type) {
            case "date.max":
              error.message = `Effective Date cannot be future dated beyond ${moment(
                error.context.limit
              ).format("L")}`;
              break;
            case "date.min":
              error.message = `Effective Date cannot be before ${moment(
                error.context.limit
              )
                .add(1, "d")
                .format("L")}`;
              break;
            default:
              return error.message;
          }
        });
        return errors;
      })
      .when("NoMedications", { is: false, then: Joi.required() }),
    ccx_stopdate: Joi.date()
      .min(new Date(medication.ccx_startdate))
      .allow("")
      .label("Stop Date")
      .error((errors) => {
        errors.forEach((error) => {
          switch (error.type) {
            case "date.min":
              error.message = `Stop date needs to be after Start Date of ${moment
                .utc(error.context.limit)
                .format("MM/DD/YYYY")}`;
              break;
            default:
              return error.message;
          }
        });
        return errors;
      }),
    NoMedications: Joi.boolean(),
  };

  useEffect(() => {
    if (!safyInfo) navigate("/");
  });

  useEffect(() => {
    return () => (isMountedRef.current = false);
  }, []);

  useEffect(() => {
    if (initialData) {
      setMedication({
        ...initialData,
        NoMedications: initialData.selectedMedication === "No Medications",
      });
      const option = {
        _id: initialData.selectedMedication,
        name: initialData.selectedMedication,
      };

      setMedicationOptions([option]);
    }
  }, [initialData]);

  const handleMedicationSearch = () => {
    const oneDay = 24 * 60 * 60 * 1000;
    const firstDate = new Date(getFieldDate(clientProgram["con.birthdate"]));
    const secondDate = new Date();
    const ageInDays = Math.round(Math.abs((firstDate - secondDate) / oneDay));

    const toastId = toast.loading("Searching Medications...", {
      type: toast.TYPE.INFO,
    });
    proxyCall(
      `https://fdb.cocentrix.com/${FDBOrderVersion}/api/FirstDatabankOrderKnowledge/OrdMeds?${searchType}=${searchQuery}&patientAgeInDays=${ageInDays}`
    )
      .then((response) => {
        toast.update(toastId, {
          render: "Search completed.",
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          autoClose: 5000,
          closeOnClick: true,
        });
        if (response && isMountedRef.current) {
          const medications = JSON.parse(response).Medications.map((med) => ({
            _id: med.Description,
            name: med.Description,
            brandName: med.BrandOrderableNames[0],
            genericName: med.GenericOrderableNames[0],
          }));

          setMedicationOptions(medications);
        }
      })
      .catch(() => {
        toast.update(toastId, {
          render: "Search failed.",
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
          closeOnClick: true,
        });

        //#region Dummy data
        // const response = {
        //   Traces: [],
        //   Medications: [
        //     {
        //       Traces: [],
        //       Status: "Active",
        //       StatusLevel: 0,
        //       Description: "AdderalL Oral",
        //       OrderableName: "AdderalL",
        //       BrandOrderableNames: ["AdderalL"],
        //       GenericOrderableNames: ["Amphetamine-Dextroamphetamine Oral"],
        //       AgeRange: {
        //         Traces: [],
        //         Description: "3 years through 110 years",
        //         MinAgeInDays: 1095,
        //         MinDescription: "3 years",
        //         MaxAgeInDays: 40150,
        //         MaxDescription: "110 years",
        //         DtoVersion: "1.0",
        //       },
        //       InFormularyOnly: false,
        //       HICSeqNos: [1792, 1795, 6719, 6720],
        //       HICLSeqNo: 13449,
        //       RtGenID: 1062025,
        //       GCNSeqNos: [4999, 5000, 5001, 34359, 47131, 47132, 47133],
        //       GCNSeqNo: 4999,
        //       DispGenID: 4999,
        //       MedIds: [157658, 158986, 167997, 181659, 266696, 279828, 297274],
        //       OrdMedId: 287,
        //       ClinicalRouteID: 212,
        //       ClinicalRoute: "oral",
        //       DosageFormID: -1,
        //       DosageForm: "N/A",
        //       NDCs: [],
        //       DtoVersion: "2.5",
        //     },
        //     {
        //       Traces: [],
        //       Status: "Active",
        //       StatusLevel: 0,
        //       Description: "Amphetamine-Dextroamphetamine Oral",
        //       OrderableName: "Amphetamine-Dextroamphetamine",
        //       BrandOrderableNames: ["AdderalL Oral"],
        //       GenericOrderableNames: ["Amphetamine-Dextroamphetamine"],
        //       AgeRange: {
        //         Traces: [],
        //         Description: "3 years through 110 years",
        //         MinAgeInDays: 1095,
        //         MinDescription: "3 years",
        //         MaxAgeInDays: 40150,
        //         MaxDescription: "110 years",
        //         DtoVersion: "1.0",
        //       },
        //       InFormularyOnly: false,
        //       HICSeqNos: [1792, 1795, 6719, 6720],
        //       HICLSeqNo: 13449,
        //       RtGenID: 1062025,
        //       GCNSeqNos: [4999, 5000, 5001, 34359, 47131, 47132, 47133],
        //       GCNSeqNo: 4999,
        //       DispGenID: 4999,
        //       MedIds: [
        //         154196, 184255, 187885, 215346, 244161, 255022, 292678, 400046,
        //         400047, 400048, 400049, 448938, 448939, 448940,
        //       ],
        //       OrdMedId: 960,
        //       ClinicalRouteID: 212,
        //       ClinicalRoute: "oral",
        //       DosageFormID: -1,
        //       DosageForm: "N/A",
        //       NDCs: [],
        //       DtoVersion: "2.5",
        //     },
        //     {
        //       Traces: [],
        //       Status: "Active",
        //       StatusLevel: 0,
        //       Description: "AdderalL Oral 24 hr Cap",
        //       OrderableName: "AdderalL",
        //       BrandOrderableNames: ["AdderalL"],
        //       GenericOrderableNames: [
        //         "Amphetamine-Dextroamphetamine Oral 24 hr Cap",
        //       ],
        //       AgeRange: {
        //         Traces: [],
        //         Description: "3 years through 110 years",
        //         MinAgeInDays: 1095,
        //         MinDescription: "3 years",
        //         MaxAgeInDays: 40150,
        //         MaxDescription: "110 years",
        //         DtoVersion: "1.0",
        //       },
        //       InFormularyOnly: false,
        //       HICSeqNos: [1792, 1795, 6719, 6720],
        //       HICLSeqNo: 13449,
        //       RtGenID: 1062025,
        //       GCNSeqNos: [48701, 48702, 48703, 50428, 50429, 50430],
        //       GCNSeqNo: 48701,
        //       DispGenID: 48701,
        //       MedIds: [209785, 220449, 260058, 443812, 443813, 443814],
        //       OrdMedId: 294,
        //       ClinicalRouteID: 212,
        //       ClinicalRoute: "oral",
        //       DosageFormID: 12,
        //       DosageForm: "Extended Release 24 hour Capsule",
        //       NDCs: [],
        //       DtoVersion: "2.5",
        //     },
        //     {
        //       Traces: [],
        //       Status: "Active",
        //       StatusLevel: 0,
        //       Description: "Amphetamine-Dextroamphetamine Oral 24 hr Cap",
        //       OrderableName: "Amphetamine-Dextroamphetamine",
        //       BrandOrderableNames: ["AdderalL Oral 24 hr Cap"],
        //       GenericOrderableNames: ["Amphetamine-Dextroamphetamine"],
        //       AgeRange: {
        //         Traces: [],
        //         Description: "3 years through 110 years",
        //         MinAgeInDays: 1095,
        //         MinDescription: "3 years",
        //         MaxAgeInDays: 40150,
        //         MaxDescription: "110 years",
        //         DtoVersion: "1.0",
        //       },
        //       InFormularyOnly: false,
        //       HICSeqNos: [1792, 1795, 6719, 6720],
        //       HICLSeqNo: 13449,
        //       RtGenID: 1062025,
        //       GCNSeqNos: [48701, 48702, 48703, 50428, 50429, 50430],
        //       GCNSeqNo: 48701,
        //       DispGenID: 48701,
        //       MedIds: [230013, 265054, 281575, 443804, 443805, 443806],
        //       OrdMedId: 967,
        //       ClinicalRouteID: 212,
        //       ClinicalRoute: "oral",
        //       DosageFormID: 12,
        //       DosageForm: "Extended Release 24 hour Capsule",
        //       NDCs: [],
        //       DtoVersion: "2.5",
        //     },
        //     {
        //       Traces: [],
        //       Status: "Active",
        //       StatusLevel: 0,
        //       Description: "AdderalL via feeding tube",
        //       OrderableName: "AdderalL",
        //       BrandOrderableNames: ["AdderalL"],
        //       GenericOrderableNames: [
        //         "Amphetamine-Dextroamphetamine via feeding tube",
        //       ],
        //       AgeRange: {
        //         Traces: [],
        //         Description: "3 years through 110 years",
        //         MinAgeInDays: 1095,
        //         MinDescription: "3 years",
        //         MaxAgeInDays: 40150,
        //         MaxDescription: "110 years",
        //         DtoVersion: "1.0",
        //       },
        //       InFormularyOnly: false,
        //       HICSeqNos: [1792, 1795, 6719, 6720],
        //       HICLSeqNo: 13449,
        //       RtGenID: 1062025,
        //       GCNSeqNos: [4999, 5000, 5001, 34359, 47131, 47132, 47133],
        //       GCNSeqNo: 4999,
        //       DispGenID: 4999,
        //       MedIds: [157658, 158986, 167997, 181659, 266696, 279828, 297274],
        //       OrdMedId: 27533,
        //       ClinicalRouteID: 174,
        //       ClinicalRoute: "feeding tube",
        //       DosageFormID: -1,
        //       DosageForm: "N/A",
        //       NDCs: [],
        //       DtoVersion: "2.5",
        //     },
        //     {
        //       Traces: [],
        //       Status: "Active",
        //       StatusLevel: 0,
        //       Description: "Amphetamine-Dextroamphetamine via feeding tube",
        //       OrderableName: "Amphetamine-Dextroamphetamine",
        //       BrandOrderableNames: ["AdderalL via feeding tube"],
        //       GenericOrderableNames: ["Amphetamine-Dextroamphetamine"],
        //       AgeRange: {
        //         Traces: [],
        //         Description: "3 years through 110 years",
        //         MinAgeInDays: 1095,
        //         MinDescription: "3 years",
        //         MaxAgeInDays: 40150,
        //         MaxDescription: "110 years",
        //         DtoVersion: "1.0",
        //       },
        //       InFormularyOnly: false,
        //       HICSeqNos: [1792, 1795, 6719, 6720],
        //       HICLSeqNo: 13449,
        //       RtGenID: 1062025,
        //       GCNSeqNos: [4999, 5000, 5001, 34359, 47131, 47132, 47133],
        //       GCNSeqNo: 4999,
        //       DispGenID: 4999,
        //       MedIds: [
        //         154196, 184255, 187885, 215346, 244161, 255022, 292678, 400046,
        //         400047, 400048, 400049, 448938, 448939, 448940,
        //       ],
        //       OrdMedId: 21501,
        //       ClinicalRouteID: 174,
        //       ClinicalRoute: "feeding tube",
        //       DosageFormID: -1,
        //       DosageForm: "N/A",
        //       NDCs: [],
        //       DtoVersion: "2.5",
        //     },
        //   ],
        //   NotProcessed: [],
        //   GcnSeqNoNotProcessed: [],
        //   MedIdNotProcessed: [],
        //   NdcNotProcessed: [],
        //   OrdMedIdNotProcessed: [],
        //   DtoVersion: "2.5",
        // };

        // const medications = response.Medications.map((med) => ({
        //   _id: med.Description,
        //   name: med.Description,
        //   brandName: med.BrandOrderableNames[0],
        //   genericName: med.GenericOrderableNames[0],
        // }));

        // setMedicationOptions(medications);
        //#endregion
      });
  };

  const handleChange = ({ currentTarget: input }) => {
    const data = { ...medication };
    data[input.name] = input.value;
    setMedication(data);

    const errs = { ...errors };
    const errorMessage = validateProperty(input);

    if (errorMessage) {
      errs[input.name] = errorMessage;
    } else {
      delete errs[input.name];
    }

    setErrors(errs);
  };

  const validateProperty = ({ name, value }) => {
    const obj = { [name]: value };
    const schema = { [name]: joiSchema[name] };
    const { error } = Joi.validate(obj, schema);
    return error ? error.details[0].message : null;
  };

  const validate = () => {
    const options = { abortEarly: false };
    const { error } = Joi.validate(medication, joiSchema, options);

    let validationErrors = {};
    if (error)
      for (let item of error.details)
        validationErrors[item.path[0]] = item.message;

    if (Object.keys(validationErrors).length === 0) return null;

    setErrors(validationErrors);
    return validationErrors;
  };

  const handleSave = () => {
    const med = medicationOptions.filter(
      (medResult) => medResult._id === medication.selectedMedication
    )[0];

    const obj = {
      ccx_patient: {
        type: "entity",
        value: {
          entityName: "contact",
          entityId: clientId,
        },
      },
      ccx_name: {
        type: "string",
        value: med.name,
      },
      ccx_brandname: {
        type: "string",
        value: med.brandName,
      },
      ccx_genericid: {
        type: "string",
        value: medication.ccx_genericid || null,
      },
      ccx_dose: {
        type: "double",
        value: medication.ccx_dose,
      },
      ccx_doseunit_global: {
        type: "optionset",
        value: parseInt(medication.ccx_doseunit_global),
      },
      ccx_quantity: {
        type: "decimal",
        value: parseFloat(medication.ccx_quantity),
      },
      ccx_route_global: {
        type: "optionset",
        value: parseInt(medication.ccx_route_global),
      },
      ccx_dosetiming: {
        type: "string",
        value: medication.ccx_dosetiming,
      },
      ccx_doseother: {
        type: "optionset",
        value: parseInt(medication.ccx_doseother),
      },
      ccx_sourceofdata: {
        type: "optionset",
        value: parseInt(medication.ccx_sourceofdata),
      },
      ccx_reportedby: {
        type: "optionset",
        value: medication.ccx_reportedby
          ? parseInt(medication.ccx_reportedby)
          : null,
      },
      ccx_startdate: {
        type: "datetime",
        value: new Date(medication.ccx_startdate),
      },
      ccx_stopdate: {
        type: "datetime",
        value: medication.ccx_stopdate
          ? new Date(medication.ccx_stopdate)
          : null,
      },
      ccx_prescriber: {
        type: "string",
        value: medication.ccx_prescriber,
      },
      ccx_patientnotes: {
        type: "string",
        value: medication.ccx_patientnotes || null,
      },
    };

    if (medication.ccx_clientmedicationid) {
      const toastId = toast.loading("Medication is being updated...", {
        type: toast.TYPE.INFO,
      });
      updateEntity(
        "ccx_clientmedication",
        medication.ccx_clientmedicationid,
        JSON.stringify(obj),
        safyInfo
      )
        .then(() => {
          toast.update(toastId, {
            render: "Medication has been updated.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
          updateMedications && updateMedications();
        })
        .catch(() => {
          toast.update(toastId, {
            render: "An error occurred while updating the Medication.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
        });
    } else {
      const toastId = toast.loading("Medication is being created...", {
        type: toast.TYPE.INFO,
      });
      createEntity(
        "ccx_clientmedication",
        JSON.stringify(obj),
        safyInfo,
        getFieldName(clientProgram.ccx_company).split(" of ")[1]
      )
        .then(() => {
          toast.update(toastId, {
            render: "Medication has been created.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
          updateMedications && updateMedications();
        })
        .catch(() => {
          toast.update(toastId, {
            render: "An error occurred while creating the Medication.",
            type: toast.TYPE.ERROR,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
        });
    }
  };

  const handleSaveAndAdd = () => {
    handleSave();
    resetForm();
  };

  const handleNoMedicationsClick = () => {
    setNoMedications(true);

    const noMedsOption = { _id: "No Medications", name: "No Medications" };
    setMedicationOptions([noMedsOption]);

    const noMedsObj = {
      selectedMedication: "No Medications",
      ccx_sourceofdata: "803080000",
      ccx_startdate: formatDate(new Date()),
      ccx_stopdate: "",
      NoMedications: true,
    };
    setMedication(noMedsObj);
  };

  const resetForm = () => {
    setMedication({
      selectedMedication: "",
      ccx_dose: "",
      ccx_doseunit_global: "",
      ccx_quantity: "",
      ccx_route_global: "",
      ccx_dosetiming: "",
      ccx_doseother: "",
      ccx_sourceofdata: "",
      ccx_reportedby: "",
      ccx_prescriber: "",
      ccx_patientnotes: "",
      ccx_startdate: "",
      ccx_stopdate: "",
      NoMedications: false,
    });
    setMedicationOptions([]);
    setSearchQuery("");
    setErrors({});
    setNoMedications(false);
  };

  const handleHideModal = () => {
    if (hideModal) {
      resetForm();
      hideModal();
    }
  };

  const searchTypeOptions = [
    {
      _id: "startsWith",
      name: "Starts with",
    },
    {
      _id: "contains",
      name: "Contains",
    },
    {
      _id: "soundsLike",
      name: "Sounds like",
    },
  ];

  const handleKeyDown = (e) => {
    if (checkKeyPress(e)) {
      handleMedicationSearch();
    }
  };

  return (
    <>
      {isOpen ? (
        <Modal show={isOpen} animation={false}>
          <Modal.Header>
            <div className="clearfix w-100">
              <div className="float-start">
                <Modal.Title>Client Medication</Modal.Title>
              </div>
              <div className="float-end pt-2">
                <button
                  className="btn btn-link"
                  onClick={handleNoMedicationsClick}
                >
                  No Medications
                </button>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body>
            <div className="container">
              {!noMedications && (
                <>
                  {!medication.ccx_clientmedicationid && (
                    <>
                      <div className="row">
                        <div className="col">
                          <Select
                            name="searchType"
                            label="Search Type"
                            options={searchTypeOptions}
                            defaultValue={searchType}
                            onChange={({ currentTarget: input }) => {
                              setSearchType(input.value);
                            }}
                          />
                          {searchType === "" && (
                            <div className="alert alert-danger">
                              'Search Type' is required.
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <input
                            name="medicationSearch"
                            label="Search for Medication"
                            placeholder="Search for Medication..."
                            value={searchQuery}
                            onChange={(e) =>
                              setSearchQuery(e.currentTarget.value)
                            }
                            onKeyDown={handleKeyDown}
                            className="form-control"
                          />
                        </div>
                        <div className="col-3">
                          {clientProgram === undefined ? (
                            <div>Client Program is being created...</div>
                          ) : null}
                          <button
                            className="btn btn-primary"
                            style={{ marginTop: "30px" }}
                            onClick={handleMedicationSearch}
                            disabled={
                              clientProgram === undefined ||
                              searchQuery === undefined ||
                              searchQuery === ""
                            }
                          >
                            Search
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
              {noMedications && (
                <div className="row">
                  <div className="col">
                    <Input
                      name="selectedMedication"
                      value={medication.selectedMedication}
                      onChange={handleChange}
                      disabled
                    />
                  </div>
                </div>
              )}
              {!noMedications && (
                <>
                  <div className="row">
                    <div className="col">
                      <Select
                        name="selectedMedication"
                        label="Medication"
                        value={medication.selectedMedication}
                        options={medicationOptions}
                        onChange={handleChange}
                        error={errors.selectedMedication}
                        disabled={
                          medicationOptions.length === 0 ||
                          medication.ccx_clientmedicationid
                        }
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Input
                        name="ccx_genericid"
                        label="Generic ID"
                        value={medication.ccx_genericid}
                        onChange={handleChange}
                        disabled={medication.ccx_clientmedicationid}
                        error={errors.ccx_genericid}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Input
                        name="ccx_dose"
                        label="Dose"
                        value={medication.ccx_dose}
                        onChange={handleChange}
                        error={errors.ccx_dose}
                        disabled={medication.ccx_clientmedicationid}
                      />
                    </div>
                    <div className="col">
                      <Select
                        name="ccx_doseunit_global"
                        label="Dose Unit"
                        value={medication.ccx_doseunit_global}
                        options={medicationOptionSets.ccx_doseunit_global}
                        onChange={handleChange}
                        error={errors.ccx_doseunit_global}
                        disabled={medication.ccx_clientmedicationid}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Input
                        name="ccx_quantity"
                        label="Quantity"
                        value={medication.ccx_quantity}
                        onChange={handleChange}
                        error={errors.ccx_quantity}
                        disabled={medication.ccx_clientmedicationid}
                      />
                    </div>
                    <div className="col">
                      <Select
                        name="ccx_route_global"
                        label="Route"
                        value={medication.ccx_route_global}
                        options={medicationOptionSets.ccx_route_global}
                        onChange={handleChange}
                        error={errors.ccx_route_global}
                        disabled={medication.ccx_clientmedicationid}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Input
                        name="ccx_dosetiming"
                        label="Dose Timing"
                        value={medication.ccx_dosetiming}
                        onChange={handleChange}
                        error={errors.ccx_dosetiming}
                        disabled={medication.ccx_clientmedicationid}
                      />
                    </div>
                    <div className="col">
                      <Select
                        name="ccx_doseother"
                        label="Dose Notes"
                        value={medication.ccx_doseother}
                        options={medicationOptionSets.ccx_doseother}
                        onChange={handleChange}
                        error={errors.ccx_doseother}
                        disabled={medication.ccx_clientmedicationid}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <RadioButton
                        name="ccx_sourceofdata"
                        label="Source of Data"
                        value={medication.ccx_sourceofdata}
                        options={medicationOptionSets.ccx_sourceofdata}
                        onChange={handleChange}
                        error={errors.ccx_sourceofdata}
                        disabled={medication.ccx_clientmedicationid}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      {medication.ccx_sourceofdata === "803080001" && (
                        <Select
                          name="ccx_reportedby"
                          label="Reported By"
                          value={medication.ccx_reportedby}
                          options={medicationOptionSets.ccx_reportedby}
                          onChange={handleChange}
                          error={errors.ccx_reportedby}
                          disabled={medication.ccx_clientmedicationid}
                        />
                      )}
                    </div>
                  </div>
                </>
              )}
              <div className="row">
                <div className="col">
                  <Input
                    type="date"
                    name="ccx_startdate"
                    label="Start Date"
                    value={
                      medication.ccx_startdate
                        ? moment
                            .utc(medication.ccx_startdate)
                            .format("YYYY-MM-DD")
                        : ""
                    }
                    onChange={handleChange}
                    error={errors.ccx_startdate}
                    disabled={medication.ccx_clientmedicationid}
                  />
                </div>
                <div className="col">
                  <Input
                    type="date"
                    name="ccx_stopdate"
                    label="Stop Date"
                    value={
                      medication.ccx_stopdate
                        ? moment
                            .utc(medication.ccx_stopdate)
                            .format("YYYY-MM-DD")
                        : ""
                    }
                    onChange={handleChange}
                    error={errors.ccx_stopdate}
                  />
                </div>
              </div>
              {!noMedications && (
                <>
                  <div className="row">
                    <div className="col">
                      <Input
                        name="ccx_prescriber"
                        label="Prescriber"
                        value={medication.ccx_prescriber}
                        onChange={handleChange}
                        error={errors.ccx_prescriber}
                        disabled={medication.ccx_clientmedicationid}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Input
                        name="ccx_patientnotes"
                        label="Reasons/Notes"
                        value={medication.ccx_patientnotes}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            {nextModal === undefined && (
              <>
                <button className="btn btn-danger" onClick={handleHideModal}>
                  Cancel
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (medication.ccx_clientmedicationid || !validate()) {
                      handleSave();
                      handleHideModal();
                    }
                  }}
                >
                  Submit
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (medication.ccx_clientmedicationid || !validate())
                      handleSaveAndAdd();
                  }}
                >
                  Submit and Add New
                </button>
              </>
            )}
            {nextModal && (
              <>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    resetForm();
                    nextModal();
                  }}
                >
                  Skip
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) {
                      handleSave();
                      resetForm();
                      nextModal();
                    }
                  }}
                >
                  Submit and {isLastModal ? "End" : "Next"}
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) handleSaveAndAdd();
                  }}
                >
                  Submit and Add New
                </button>
              </>
            )}
          </Modal.Footer>
        </Modal>
      ) : null}
    </>
  );
}

export default MedicationModal;
